/**
 * Experiment Element Visibility Types
 * 
 * Shared types for experiment-driven element visibility system.
 * Context: Address Page
 */

import { AllowedExperimentKey } from '@tenants/types';

/**
 * Viewport type
 */
export type Viewport = 'mobile' | 'desktop';

/**
 * Element identifiers for experiment-controlled elements.
 * Context: Address Page
 * Every renderable element gets a unique ID.
 */
export enum ExperimentElement {
  // Carousel elements
  CAROUSEL_SHARE_BUTTON = 'carousel.shareButton',
  CAROUSEL_LIKE_BUTTON = 'carousel.likeButton',
  
  // Hero section elements
  HERO_BREADCRUMBS = 'hero.breadcrumbs',
  HERO_ADDRESS = 'hero.address',
  HERO_PRICE = 'hero.price',
  HERO_LAYOUT_V2 = 'hero.layoutV2',
  HERO_SHARE_BUTTON = 'hero.shareButton',
  HERO_LIKE_BUTTON = 'hero.likeButton',
  
  // Sticky header elements
  STICKY_HEADER_TABS = 'stickyHeader.tabs',
  STICKY_HEADER_SHARE_BUTTON = 'stickyHeader.shareButton',
  STICKY_HEADER_LIKE_BUTTON = 'stickyHeader.likeButton',
  
  // Listing content elements
  OPEN_HOUSE_CARD = 'content.openHouseCard', // New standalone card (v2), legacy has it built-in
  
  // Footer elements
  STICKY_FOOTER = 'footer.stickyFooter', // Mobile sticky CTA buttons (v2 only)
  
  // Content sections
  SECTION_KEY_FACTS = 'section.keyFacts',
  SECTION_DESCRIPTION = 'section.description',
  SECTION_ROOMS = 'section.rooms',
  SECTION_MAP = 'section.map',
  SECTION_PRICE_HISTORY = 'section.priceHistory',
  SECTION_AGENT_CARDS = 'section.agentCards',
  SECTION_PROPERTY_APPRAISAL = 'section.propertyAppraisal',
  SECTION_CHARTS = 'section.charts',
  SECTION_SCHOOLS = 'section.schools',
  SECTION_DEMOGRAPHICS = 'section.demographics',
  SECTION_COMMUTE = 'section.commute',
  SECTION_SIMILAR_LISTINGS = 'section.similarListings',
  SECTION_COMPARABLE_LISTINGS = 'section.comparableListings',
  
  // Sidebar elements
  SIDEBAR_CONNECTION_REQUEST = 'sidebar.connectionRequest',
  SIDEBAR_FAVOURITE_PROMPT = 'sidebar.favouritePrompt',
  SIDEBAR_MLML_BANNER = 'sidebar.mlmlBanner',
  SIDEBAR_MLML_CREATE = 'sidebar.mlmlCreate',
  SIDEBAR_SOLD_EXPERIMENT_CARD = 'sidebar.soldExperimentCard',
  
  // Footer elements
  FOOTER_INTERNAL_LINKS = 'footer.internalLinks',
  
  // Banners
  BANNER_TERMS_OF_USE = 'banner.termsOfUse',
  BANNER_PROVIDER = 'banner.provider',
}

/**
 * Active experiments state
 */
export interface ExperimentState {
  [AllowedExperimentKey.LISTING_HERO]?: boolean;
  [AllowedExperimentKey.STICKY_HEADER_TABS]?: boolean;
  [AllowedExperimentKey.CONNECTION_REQUEST_FORM]?: boolean;
  [AllowedExperimentKey.ZOOPRAISAL_MORTGAGE_CTA]?: boolean;
}

/**
 * Visibility context - includes experiments and viewport
 */
export interface VisibilityContext {
  experiments: ExperimentState;
  viewport: Viewport;
}

/**
 * Declarative visibility rule
 * 
 * Simple, readable format:
 * - hideWhen: experiments that should hide this element
 * - showWhen: experiments that should show this element (overrides default)
 * - viewports: which viewports this rule applies to
 */
export interface VisibilityRule {
  // Hide element when ANY of these experiments are active
  hideWhen?: {
    experiments: AllowedExperimentKey[];
    viewports?: Viewport[]; // Which viewports this hide rule applies to
  };
  
  // Show element ONLY when ANY of these experiments are active
  showWhen?: {
    experiments: AllowedExperimentKey[];
    viewports?: Viewport[]; // Which viewports this show rule applies to
  };
}

/**
 * Rule can be either declarative (VisibilityRule) or a custom function
 */
export type Rule = VisibilityRule | ((context: VisibilityContext) => boolean);
